import React, { useState, useContext, useRef, useEffect } from 'react';
import './EntrepreneurInfo.css';
import Sidebar from '../../components/Sidebar/Sidebar';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ValuationContext } from '../../context/ValuationContext';
import { getSectorOptions, getCompanyStages, isDcfOnlyCompanyStage } from '../sectorOptions';

const EntrepreneurInfo = () => {
    const { t, i18n } = useTranslation();
    const { valuationData, updateEntrepreneurInfo, setCurrency, setValuationCurrency } = useContext(ValuationContext);
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const sectors = getSectorOptions(t, i18n.language);
    const stages = getCompanyStages(i18n.language);
    const [formData, setFormData] = useState(
        valuationData.entrepreneurInfo || {
            companyName: '',
            sector: '',
            stage: '',
            foundingDate: '',
            currency: '',
            valuationCurrency: '',
            companyLogo: null
        }
    );
    const isDcfOnly = isDcfOnlyCompanyStage(formData.stage);
    const [logoPreview, setLogoPreview] = useState(valuationData.entrepreneurInfo?.companyLogo || null);
    const [logoError, setLogoError] = useState('');
    const [showAccessModal, setShowAccessModal] = useState(false);

    const handleChange = (e) => {
        const { id, value } = e.target;
        
        if (id === 'currency') {
            setFormData({ ...formData, [id]: value });
            setCurrency(value);
        } else if (id === 'valuationCurrency') {
            setFormData({ ...formData, [id]: value });
            setValuationCurrency(value);
        } else {
            setFormData({ ...formData, [id]: value });
        }
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        setLogoError('');

        if (!file) {
            return;
        }

        // Dosya tipi kontrolü
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setLogoError(t('entrepreneurInfo.logoError'));
            return;
        }

        // Dosya boyutu kontrolü (5MB)
        if (file.size > 5 * 1024 * 1024) {
            setLogoError(t('entrepreneurInfo.logoSizeError'));
            return;
        }

        // Resmi base64'e çevir
        const reader = new FileReader();
        reader.onload = (event) => {
            const base64String = event.target.result;
            setLogoPreview(base64String);
            setFormData({ ...formData, companyLogo: base64String });
        };
        reader.onerror = () => {
            setLogoError(t('entrepreneurInfo.logoReadError'));
        };
        reader.readAsDataURL(file);
    };

    const removeLogo = () => {
        setLogoPreview(null);
        setFormData({ ...formData, companyLogo: null });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };
    const [isSaving, setIsSaving] = useState(false);

    // Sayfa yüklendiğinde scroll'u en üste al
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Sayfa yüklendiğinde draft verilerini getir
    useEffect(() => {
        checkUserAccess();
        loadDraftData();
        loadFinancialData();
    }, []);

    const checkUserAccess = async () => {
        try {
            const token = localStorage.getItem('userToken');

            // Kullanıcı bilgilerini al
            const userResponse = await fetch('/api/user/me', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!userResponse.ok) {
                navigate('/shop');
                return;
            }

            const userData = await userResponse.json();
            const user = userData.user;

            // Sınırsız paket kontrolü
            const hasUnlimitedPackage = user.unlimitedPackageExpiresAt &&
                new Date(user.unlimitedPackageExpiresAt) > new Date();

            // Draft kontrolü
            const draftResponse = await fetch('/api/drafts/current', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const draftData = await draftResponse.json();
            const hasDraft = draftData.exists;

            // Sınırsız paket yoksa ve token 0 ise ve draft yoksa yönlendir
            if (!hasUnlimitedPackage && (user.token <= 0) && !hasDraft) {
                setShowAccessModal(true);
            }
        } catch (error) {
            console.error('Kullanıcı erişim kontrolü hatası:', error);
        }
    };

    const loadDraftData = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch('/api/drafts/step/entrepreneur-info', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.exists && data.data) {
                    setFormData(prevData => ({ ...prevData, ...data.data }));
                    if (data.data.companyLogo) {
                        setLogoPreview(data.data.companyLogo);
                    }
                }
            }
        } catch (error) {
            console.error('Draft verisi yüklenirken hata:', error);
        }
    };

    const loadFinancialData = async () => {
    try {
        const token = localStorage.getItem('userToken');
        const response = await fetch('/api/drafts/step/financial-table', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (response.ok) {
            const data = await response.json();
            if (data.exists && data.data && data.data.inputs) {
                // Financial table verilerini context'e yükle
                updateFinancials(data.data.inputs, data.data.results || {});
            }
        }
    } catch (error) {
        console.error('Financial draft verisi yüklenirken hata:', error);
    }
};

    const saveDraft = async () => {
        setIsSaving(true);
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch('/api/drafts/save', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    step: 'entrepreneur-info',
                    data: formData
                })
            });
            
            if (response.ok) {
                alert(t('entrepreneurInfo.saveSuccess'));
            } else {
                throw new Error('Kaydetme başarısız');
            }
        } catch (error) {
            console.error('Kaydetme hatası:', error);
            alert(t('entrepreneurInfo.saveError'));
        } finally {
            setIsSaving(false);
        }
    };
    const handleNext = async (e) => {
    e.preventDefault();
    
    // Önce draft olarak kaydet
    try {
        const token = localStorage.getItem('userToken');
        await fetch('/api/drafts/save', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                step: 'entrepreneur-info',
                data: formData
            })
        });
    } catch (error) {
        console.error('Otomatik kaydetme hatası:', error);
        // Hata olsa bile devam et
    }
    
    // Sonra context'i güncelle ve ilerle
    updateEntrepreneurInfo(formData);
    navigate('/financial-table');
};

    return (
        <div className="entrepreneur-page">
            <Sidebar />

            {/* Access Modal */}
            {showAccessModal && (
                <div className="modal-overlay" onClick={() => {
                    setShowAccessModal(false);
                    navigate('/shop');
                }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{t('entrepreneurInfo.accessModalTitle')}</h3>
                        </div>
                        <div className="modal-body">
                            <p>{t('entrepreneurInfo.accessModalDesc1')}</p>
                            <p>{t('entrepreneurInfo.accessModalDesc2')}</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="modal-btn-primary"
                                onClick={() => {
                                    setShowAccessModal(false);
                                    navigate('/shop');
                                }}
                            >
                                {t('entrepreneurInfo.goToShop')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="info-form-container">
                {!isDcfOnly && <div className="progress-section">
                    <ProgressBar currentStep={1} />
                </div>}
                
                <h2>{t('entrepreneurInfo.title')}</h2>
                <p>{t('entrepreneurInfo.subtitle')}</p>

                <form onSubmit={handleNext} className="info-form">
                    <div className="top-form-section">
                        <div className="company-info-section">
                            <div className="form-group">
                                <label htmlFor="companyName">{t('entrepreneurInfo.companyName')}</label>
                                <input
                                    type="text"
                                    id="companyName"
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    placeholder={t('entrepreneurInfo.companyNamePlaceholder')}
                                    required
                                />
                            </div>

                           <div className="form-group">
                                <label htmlFor="sector">{t('entrepreneurInfo.sector')}</label>
                                <select id="sector" value={formData.sector} onChange={handleChange} required>
                                    <option value="" disabled>{t('entrepreneurInfo.selectSector')}</option>
                                    {sectors.map((sector, index) => (
                                        <option key={index} value={sector.key}>{sector.value}</option>
                                    ))}
                                </select>
                            </div>


                            <div className="form-group">
                                <label htmlFor="stage">{i18n.language?.startsWith('en') ? 'Company Stage' : 'Şirket Aşaması'}</label>
                                <select id="stage" value={formData.stage} onChange={handleChange} required>
                                    <option value="" disabled>{i18n.language?.startsWith('en') ? 'Select company stage' : 'Şirket aşamasını seçin'}</option>
                                    {stages.map((stage) => (
                                        <option key={stage.key} value={stage.value}>{stage.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="logo-section">
                            <div className="form-group">
                                <label>{t('entrepreneurInfo.companyLogo')}</label>
                                <div className="logo-upload-container">
                                    <div className="logo-preview-area">
                                        {logoPreview ? (
                                            <div className="logo-preview">
                                                <img src={logoPreview} alt="Company logo" />
                                                <button
                                                    type="button"
                                                    className="remove-logo-btn"
                                                    onClick={removeLogo}
                                                    title={t('entrepreneurInfo.removeLogo')}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="logo-placeholder" onClick={triggerFileInput}>
                                                <div className="upload-icon">📁</div>
                                                <p>{t('entrepreneurInfo.uploadLogo')}</p>
                                                <span className="upload-hint">JPG, PNG, GIF, WebP (Max 5MB)</span>
                                            </div>
                                        )}
                                    </div>

                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleLogoUpload}
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                    />

                                    {logoError && (
                                        <div className="logo-error">{logoError}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="foundingDate">{t('entrepreneurInfo.foundingDate')}</label>
                            <input
                                type="date"
                                id="foundingDate"
                                value={formData.foundingDate}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="currency">{t('entrepreneurInfo.financialCurrency')}</label>
                                <select id="currency" value={formData.currency} onChange={handleChange} required>
                                <option value="" disabled>{t('entrepreneurInfo.selectCurrency')}</option>
                                <option value="₺">₺ (TRY)</option>
                                <option value="€">€ (EUR)</option>
                                <option value="$">$ (USD)</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="valuationCurrency">{t('entrepreneurInfo.valuationCurrency')}</label>
                        <select id="valuationCurrency" value={formData.valuationCurrency} onChange={handleChange} required>
                             <option value="" disabled>{t('entrepreneurInfo.selectCurrency')}</option>
                            <option value="₺">₺ (TRY)</option>
                            <option value="€">€ (EUR)</option>
                            <option value="$">$ (USD)</option>
                        </select>
                    </div>

                     <div className="navigation-buttons">
                        <button
                            type="button"
                            className="save-draft-btn"
                            onClick={saveDraft}
                            disabled={isSaving}
                        >
                            {isSaving ? t('entrepreneurInfo.saving') : t('entrepreneurInfo.saveForLater')}
                        </button>

                        <div className="nav-buttons-group">
                            <button type="button" className="nav-button back-button" disabled>
                                ← {t('entrepreneurInfo.previousStep')}
                            </button>
                            <button type="submit" className="nav-button next-button">
                                {t('entrepreneurInfo.nextStep')} →
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EntrepreneurInfo;
